import React, { useEffect, useMemo, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import Content from 'studio/components/Content/Content';
import Typography from '@mui/material/Typography';
import PageTitle from 'studio/components/PageTitle';
import Dashboard from '@mui/icons-material/Dashboard';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import { trackEvent } from 'utils/eventTracking';
import Button from '@mui/material/Button';
import DataGrid from 'studio/components/DataGrid/DataGrid';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import UserChip from 'studio/components/Chip/UserChip/UserChip';
import SharingDetails from 'studio/pages/RoleManagement/SharingDetails';
import services from 'services';
import BoardNameCell from 'studio/pages/BoardTransfer/BoardNameCell';
import UserSelector from 'studio/components/UserSelector/UserSelector';
import { fetchAllRoles, fetchAllUsers, getUserLabel } from 'utils/userRoleUtils';
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowForward } from '@mui/icons-material';
import { GridFilterInputValue } from '@mui/x-data-grid';

const styles = theme => ({
  transferSection: {
    marginTop: theme.spacing(2)
  },
  actionButton: {
    marginTop: theme.spacing(2),
    position: 'relative'
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  boardsGrid: {
    marginTop: theme.spacing(2)
  },
  arrowContainer: {
    marginTop: theme.spacing(1)
  },
  userSelectorContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2)
  },
  userSelector: {
    flex: '1 0'
  }
});

const BoardTransfer = ({ classes }) => {
  const { successMessage, errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();
  const { showConfirmationModal } = useConfirmationModal();

  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState(null);
  const [sourceUser, setSourceUser] = useState(null);
  const [targetUser, setTargetUser] = useState(null);
  const [boards, setBoards] = useState([]);
  const [selectedBoards, setSelectedBoards] = useState([]);
  const [isTransferring, setIsTransferring] = useState(false);

  useEffect(() => {
    trackEvent('Admin Board Transfer Opened');
    getAllUsersWithDeleted();
    getAllRoles();
  }, []);

  useEffect(() => {
    if (sourceUser) {
      getUserBoards(sourceUser.id);
    } else {
      setBoards([]);
    }
    setSelectedBoards([]);
  }, [sourceUser]);

  const getAllRoles = async () => {
    const roles = await fetchAllRoles(withLoader, errorMessage);
    setRoles(roles);
  };

  const getAllUsersWithDeleted = async () => {
    const users = await fetchAllUsers(withLoader, errorMessage, true);
    setUsers(users);
  };

  const getUserBoards = async userId => {
    const response = await withLoader(services.getUserBoards(userId));
    if (response.success) {
      setBoards(response.data.data || []);
    } else {
      const message = response?.response?.data?.data?.message || 'Failed to load boards';
      errorMessage(message);
      setBoards([]);
    }
  };

  const validateTransfer = () => {
    if (!sourceUser || !targetUser) {
      errorMessage('Please select both source and target users');
      return false;
    }

    if (sourceUser.id === targetUser.id) {
      errorMessage('Source and target users must be different');
      return false;
    }

    if (!selectedBoards.length) {
      errorMessage('Please select at least one board to transfer');
      return false;
    }

    return true;
  };

  const handleTransfer = () => {
    if (!validateTransfer()) {
      return;
    }
    showConfirmationModal(() => ({
      title: 'Transfer Boards',
      content: `Are you sure you want to transfer ${selectedBoards.length} selected board(s) from ${getUserLabel(
        sourceUser
      )} to ${getUserLabel(targetUser)}?`,
      confirmButtonText: 'Transfer',
      onConfirm: async () => {
        setIsTransferring(true);
        try {
          const response = await withLoader(services.transferBoards(targetUser.id, selectedBoards));

          if (response.success) {
            successMessage(`Successfully transferred ${response.data.data.transferredCount} board(s)`);
            getUserBoards(sourceUser.id);
            trackEvent('Admin Board Transfer Completed', {
              boardCount: selectedBoards.length,
              selectedBoards,
              sourceUser,
              targetUser
            });
            setSelectedBoards([]);
          } else {
            const message = response?.response?.data?.data?.message || 'Failed to transfer boards';
            errorMessage(message);
            trackEvent('Admin Board Transfer Failed', {
              error: message,
              selectedBoards,
              sourceUser,
              targetUser
            });
          }
        } catch (error) {
          errorMessage('An unexpected error occurred during transfer');
          console.error('Transfer error:', error);
          trackEvent('Admin Board Transfer Error', {
            error: error.message
          });
        } finally {
          setIsTransferring(false);
        }
      }
    }));
  };

  const boardColumns = useMemo(
    () => [
      {
        field: 'names',
        headerName: 'Board Name',
        flex: 1,
        minWidth: 200,
        renderCell: params => <BoardNameCell names={params.row.names} />,
        // value getter so we can actually sort by the board name (and filter by them) due to the different languages
        valueGetter: params => {
          if (!params.row.names) return '';
          return Object.values(params.row.names).join(' ');
        }
      },
      {
        field: 'kgId',
        headerName: 'Knowledge Graph ID',
        flex: 1,
        minWidth: 200,
        renderCell: params => (
          <Typography variant="body2" noWrap>
            {params.value}
          </Typography>
        )
      },
      {
        field: 'sharedBoard',
        headerName: 'Shared with',
        flex: 1,
        minWidth: 200,
        renderCell: params => <SharingDetails sharedBoard={params.value} roles={roles} users={users} />,
        sortComparator: (v1, v2, param1, param2) => {
          // Get the total number of shares (users + roles) for sorting
          const getShareCount = params => {
            const sharedBoard = params.value;
            if (!sharedBoard) return 0;
            const userCount = sharedBoard.userIds?.length || 0;
            const roleCount = sharedBoard.roleIds?.length || 0;
            return userCount + roleCount + (sharedBoard.isSharedWithOrg ? 1 : 0);
          };
          return getShareCount(param1) - getShareCount(param2);
        },
        // we need to do a custom filter as with this version of x-data-grid we can't (at least easily) have the default filters AND custom sorting
        // could revisit this if we upgrade the x-data-grid version
        filterOperators: [
          {
            label: 'Contains',
            value: 'contains',
            getApplyFilterFn: filterItem => {
              if (!filterItem.value) {
                return null;
              }

              return params => {
                const sharedBoard = params.row.sharedBoard;
                if (!sharedBoard) return false;

                const searchTerm = filterItem.value.toLowerCase();

                // Check users
                const matchingUsers = users
                  ?.filter(u => sharedBoard.userIds?.includes(u.id))
                  ?.some(u => getUserLabel(u).toLowerCase().includes(searchTerm));

                // Check roles
                const matchingRoles = roles
                  ?.filter(r => sharedBoard.roleIds?.includes(r.id))
                  ?.some(r => r.name.toLowerCase().includes(searchTerm));

                // Check org
                const matchesOrg = sharedBoard.isSharedWithOrg && 'organization'.includes(searchTerm);

                return matchingUsers || matchingRoles || matchesOrg;
              };
            },
            InputComponent: GridFilterInputValue
          }
        ]
      },
      {
        field: 'createdOn',
        headerName: 'Created',
        width: 180,
        renderCell: params => (
          <Typography variant="body2" noWrap>
            {new Date(params.value).toLocaleString()}
          </Typography>
        )
      },
      {
        field: 'lastModified',
        headerName: 'Last Modified',
        width: 180,
        renderCell: params => (
          <Typography variant="body2" noWrap>
            {new Date(params.value).toLocaleString()}
          </Typography>
        )
      }
    ],
    [roles, users]
  );

  const transferEnabled = sourceUser && targetUser && !loading && !isTransferring;

  return (
    <Content loading={!users}>
      <PageTitle icon={Dashboard}>Board Transfer</PageTitle>

      <Typography variant="body1">
        Transfer board ownership between users. You can transfer all boards from one user to another, or select specific
        boards to transfer.
      </Typography>

      <div className={classes.transferSection}>
        <div className={classes.userSelectorContainer}>
          <UserSelector
            value={sourceUser}
            onChange={(_, value) => setSourceUser(value)}
            className={classes.userSelector}
            label="Source User"
            description="Select the user whose boards you want to transfer"
            users={users}
            disabled={loading || isTransferring}
          />
          <ArrowForward className={classes.arrowContainer} />
          <UserSelector
            value={targetUser}
            onChange={(_, value) => setTargetUser(value)}
            className={classes.userSelector}
            label="Target User"
            description="Select the user who should receive the boards"
            users={users?.filter(u => !u.isDeleted)}
            disabled={loading || isTransferring}
          />
        </div>
      </div>

      {sourceUser && (
        <div className={classes.boardsGrid}>
          <Typography variant="h6" gutterBottom>
            Boards owned by <UserChip user={sourceUser} />
          </Typography>
          <DataGrid
            rows={boards}
            columns={boardColumns}
            pageSize={10}
            checkboxSelection
            selectionModel={selectedBoards}
            onSelectionModelChange={newSelection => {
              const selection = newSelection.selectionModel || newSelection;
              setSelectedBoards(selection);
            }}
            autoHeight
            getRowId={row => row.id}
            noRowsText="No boards found"
          />
        </div>
      )}

      <Button
        variant="contained"
        color="primary"
        className={classes.actionButton}
        onClick={handleTransfer}
        disabled={!transferEnabled}
        startIcon={<ArrowForward />}
      >
        Transfer Boards
        {isTransferring && <CircularProgress size={24} className={classes.buttonProgress} />}
      </Button>
    </Content>
  );
};

export default withStyles(styles)(BoardTransfer);
